'use client';

import { type FieldKind, FieldKind as Kind, ValueShape as Shape, type ValueShape } from '@inixiative/json-rules';
import { Checkbox } from '@zealot/ui/components/checkbox';
import { ComboboxMultiSelect } from '@zealot/ui/components/combobox-multi-select';
import type { ComboboxSelectItem } from '@zealot/ui/components/combobox-select';
import { DatePicker } from '@zealot/ui/components/date-picker';
import { Input } from '@zealot/ui/components/input';
import { SimpleSelect } from '@zealot/ui/components/simple-select';
import { Textarea } from '@zealot/ui/components/textarea';
import { VocabularySelect } from '@zealot/ui/components/vocabulary-select';
import { cn } from '@zealot/ui/lib/utils';
import { X } from 'lucide-react';
import type * as React from 'react';
import { useState } from 'react';

const BOOLEAN_ITEMS = [
  { value: 'true', label: 'True' },
  { value: 'false', label: 'False' },
];

type ValueOption = ComboboxSelectItem;

// gloss
export type ValueInputSpec = {
  kind: FieldKind;
  shape?: ValueShape;
  options?: ValueOption[];
  multiline?: boolean;
  rows?: number;
  inputType?: 'text' | 'email' | 'tel' | 'url';
  step?: number | 'any';
  prefix?: string;
  suffix?: string;
  as?: 'checkbox' | 'select';
  showClear?: boolean;
  displayFormat?: string;
};

const NUMBER_INPUT_KINDS: readonly FieldKind[] = [Kind.Int, Kind.Float];

// gloss
export type ValueInputValue = string | number | boolean | string[] | Date | null;

// gloss
export type ValueInputProps = {
  spec: ValueInputSpec;
  value: ValueInputValue;
  onChange: (next: ValueInputValue) => void;
  placeholder?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  size?: 'sm' | 'default' | 'lg';
  className?: string;
  'aria-label'?: string;
  onBlur?: () => void;
  onKeyDown?: (event: React.KeyboardEvent) => void;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  dataEmpty?: boolean;
};

const asText = (value: ValueInputValue): string => (value == null ? '' : String(value));

// gloss
const controlFor = (spec: ValueInputSpec) => {
  if (spec.kind === Kind.Boolean) return 'boolean';
  if (spec.kind === Kind.DateTime) return 'date';
  if (spec.shape === Shape.array || spec.shape === Shape.dayList) return spec.options ? 'multiselect' : 'chips';
  if (spec.options) return 'select';
  if (NUMBER_INPUT_KINDS.includes(spec.kind)) return 'number';
  if (spec.multiline) return 'textarea';
  return 'text';
};

// gloss
const ChipsInput = ({
  value,
  onChange,
  numeric,
  className,
  'aria-label': ariaLabel,
}: {
  value: string[];
  onChange: (value: string[]) => void;
  numeric: boolean;
  className?: string;
  'aria-label'?: string;
}) => {
  const [draft, setDraft] = useState('');
  const commit = () => {
    const next = draft.trim();
    setDraft('');
    if (next && !value.includes(next)) onChange([...value, next]);
  };
  return (
    <div
      className={cn(
        'flex min-w-44 flex-wrap items-center gap-1 rounded-md border border-input bg-background px-2 py-1 text-sm',
        className,
      )}
    >
      {value.map((entry) => (
        <span key={entry} className="inline-flex items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-xs">
          {entry}
          <button
            type="button"
            aria-label={`Remove ${entry}`}
            onClick={() => onChange(value.filter((held) => held !== entry))}
            className="text-muted-foreground transition-colors hover:text-destructive"
          >
            <X className="size-3" />
          </button>
        </span>
      ))}
      <input
        aria-label={ariaLabel ?? 'Add value'}
        type={numeric ? 'number' : 'text'}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ',') {
            event.preventDefault();
            commit();
          } else if (event.key === 'Backspace' && !draft && value.length) {
            onChange(value.slice(0, -1));
          }
        }}
        onBlur={commit}
        placeholder={value.length ? '' : 'type a value, Enter'}
        className="min-w-20 flex-1 bg-transparent outline-none placeholder:text-muted-foreground"
      />
    </div>
  );
};

// gloss
export const ValueInput = ({
  spec,
  value,
  onChange,
  placeholder,
  disabled,
  autoFocus,
  size = 'default',
  className,
  'aria-label': ariaLabel,
  onBlur,
  onKeyDown,
  defaultOpen,
  onOpenChange,
  dataEmpty,
}: ValueInputProps) => {
  const control = controlFor(spec);
  switch (control) {
    case 'chips':
      return (
        <ChipsInput
          value={Array.isArray(value) ? value : []}
          onChange={onChange}
          numeric={NUMBER_INPUT_KINDS.includes(spec.kind)}
          className={className}
          aria-label={ariaLabel}
        />
      );

    case 'textarea':
      return (
        <Textarea
          autoFocus={autoFocus}
          rows={spec.rows ?? 4}
          value={asText(value)}
          onChange={(event) => onChange(event.target.value)}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          size={size}
          className={className}
          aria-label={ariaLabel}
        />
      );

    case 'number':
      return (
        <Input
          autoFocus={autoFocus}
          type="number"
          step={spec.step}
          value={asText(value)}
          onChange={(event) => onChange(event.target.value)}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          startIcon={spec.prefix ? <span>{spec.prefix}</span> : undefined}
          endIcon={spec.suffix ? <span>{spec.suffix}</span> : undefined}
          placeholder={placeholder}
          disabled={disabled}
          size={size}
          data-empty={dataEmpty || undefined}
          className={cn(className, dataEmpty && 'border-dashed')}
          aria-label={ariaLabel}
        />
      );

    case 'select':
      return (
        <VocabularySelect
          items={spec.options ?? []}
          showClear={spec.showClear}
          value={value == null ? null : String(value)}
          onValueChange={onChange}
          placeholder={placeholder ?? 'Select…'}
          disabled={disabled}
          size={size}
          className={className}
          defaultOpen={defaultOpen}
          onOpenChange={onOpenChange}
          aria-label={ariaLabel ?? 'Value'}
        />
      );

    case 'multiselect':
      return (
        <ComboboxMultiSelect
          items={spec.options ?? []}
          value={Array.isArray(value) ? value : []}
          onValueChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          size={size}
          className={className}
          defaultOpen={defaultOpen}
          onOpenChange={onOpenChange}
          aria-label={ariaLabel}
        />
      );

    case 'boolean':
      return spec.as === 'select' ? (
        <SimpleSelect
          items={BOOLEAN_ITEMS}
          value={value == null ? null : String(value)}
          onValueChange={(next) => next && onChange(next === 'true')}
          placeholder={placeholder ?? 'value'}
          disabled={disabled}
          size={size}
          className={className}
          aria-label={ariaLabel ?? 'Value'}
        />
      ) : (
        <Checkbox
          checked={value === true}
          onCheckedChange={(checked) => onChange(checked)}
          disabled={disabled}
          size={size}
          className={className}
          aria-label={ariaLabel}
        />
      );

    case 'date':
      return (
        <DatePicker
          isClearable
          hasTime={spec.shape !== Shape.dateValue}
          displayFormat={spec.displayFormat}
          value={value instanceof Date ? value : null}
          defaultMonth={value instanceof Date ? value : undefined}
          onValueChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          size={size}
          className={className}
          defaultOpen={defaultOpen}
          onOpenChange={onOpenChange}
          aria-label={ariaLabel}
        />
      );

    default:
      return (
        <Input
          autoFocus={autoFocus}
          type={spec.inputType ?? 'text'}
          value={asText(value)}
          onChange={(event) => onChange(event.target.value)}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          size={size}
          data-empty={dataEmpty || undefined}
          className={cn(className, dataEmpty && 'border-dashed')}
          aria-label={ariaLabel}
        />
      );
  }
};
